import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { confirmClaim, sendClaimForConfirmation } from '../../../services/sandbox.js';

// The service is called through its import at call time, not captured here.
const ACTIONS = {
  confirm: { call: (args) => confirmClaim(args), patch: { status: 'confirmed' }, toast: 'confirmed' },
  send: { call: (args) => sendClaimForConfirmation(args), patch: { sent: true }, toast: 'sent' },
};

/**
 * Confirm or Send for confirmation on one Sandbox claim (CT-5718). The row moves
 * at once (`patchClaim`) and moves back on failure, with a toast either way. One
 * request per claim at a time (`submittingRef` holds the ids in flight).
 */
export function useClaimAction({ entityType, entityId, patchClaim, onDone } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [runningIds, setRunningIds] = useState(() => new Set());
  const submittingRef = useRef(new Set());

  const setRunning = useCallback((claimId, running) => {
    setRunningIds((current) => {
      const next = new Set(current);
      if (running) next.add(claimId);
      else next.delete(claimId);
      return next;
    });
  }, []);

  const run = useCallback(
    async (claim, kind) => {
      const action = ACTIONS[kind];
      if (!action || submittingRef.current.has(claim.id)) return false;
      submittingRef.current.add(claim.id);
      setRunning(claim.id, true);
      const previous = { status: claim.status, sent: claim.sent };
      patchClaim?.(claim.id, action.patch);
      try {
        await action.call({ entityType, entityId, claimId: claim.id });
        toast.success({ title: t(`knowledge.sandbox.toast.${action.toast}Title`), message: claim.label });
        onDone?.();
        return true;
      } catch (err) {
        patchClaim?.(claim.id, previous);
        toast.error({ title: t('knowledge.sandbox.toast.errorTitle'), message: err?.message ?? t('knowledge.sandbox.toast.errorMessage') });
        return false;
      } finally {
        submittingRef.current.delete(claim.id);
        setRunning(claim.id, false);
      }
    },
    [entityType, entityId, patchClaim, onDone, setRunning, toast, t],
  );

  return useMemo(() => ({ run, runningIds }), [run, runningIds]);
}

export default useClaimAction;
